import { Request } from '@/api/Request'
import { usePathFromBaseURI, useQuery } from '@/api/configuration/endpoints'
import { emptyIconLink } from '@/infranstructure/constants'
import { TelegramUserData, VkUserData } from '@/infranstructure/types'
import { useAsyncState } from '@/infranstructure/useAsyncState'
import { AxiosError } from 'axios'
import { ConfirmationLinkResponse, ConfirmationPayload, ConfirmationPhoneResponse, CreateLinkCommand,
  CreatePayloadCommand, VerifyCodeCommand, VerifyCodeResponse, VerifyPhoneCodeResponse } from './types'
import { PostRequest } from '@/api/PostRequest'


const defaultVkUserData = { id: 0, title: '', photoUri: emptyIconLink.href, isMessagesFromGroupAllowed: false }

export const useVkUserQuery = (transactionId: string, userId: string) => useAsyncState<VkUserData, number[]>(async (identityValue) => {
  if (identityValue === 0) return defaultVkUserData
  const endpointUri = useQuery(() => usePathFromBaseURI('avissign', 'users', 'VkUser', identityValue.toString()),
    { name: 'transactionId', value: transactionId }, { name: 'userId', value: userId })
  const query = new Request<VkUserData>(endpointUri.href)
  const response = await query.response().catch(ex => {
    const axiosEx = ex as AxiosError
    if (!axiosEx.isAxiosError || axiosEx.response?.status !== 404) throw ex
    return { data: defaultVkUserData }
  })
  const result = response.data ? response.data : defaultVkUserData
  return result
}, defaultVkUserData, {
  immediate: false,
  onError(ex) {
    console.warn(ex)
  },
})

const defaultTelegramUserData = { id: 0, title: '', photo: '', isMessagesFromGroupAllowed: false }

export const useTelegramUserQuery = (transactionId: string, userId: string) => useAsyncState<TelegramUserData, number[]>(async (identityValue) => {
  if (identityValue === 0) return defaultTelegramUserData
  const endpointUri = useQuery(() => usePathFromBaseURI('avissign', 'users', 'TelegramUser', identityValue.toString()),
    { name: 'transactionId', value: transactionId }, { name: 'userId', value: userId })
  const query = new Request<TelegramUserData>(endpointUri.href)
  const response = await query.response().catch(ex => {
    const axiosEx = ex as AxiosError
    if (!axiosEx.isAxiosError || axiosEx.response?.status !== 404) throw ex
    return { data: defaultTelegramUserData }
  })
  const result = response.data ? response.data : defaultTelegramUserData
  return result
}, defaultTelegramUserData, {
  immediate: false,
  onError(ex) {
    console.warn(ex)
  },
})

export const useVerifyCodeCommand = (apiKey: string, onError: (ex: unknown) => void) => useAsyncState<VerifyCodeResponse, VerifyCodeCommand[]>(async (command) => {
  const endpointUri = useQuery(() => usePathFromBaseURI('avissign', 'Confirmation', 'VerifyCode'),
    { name: 'key', value: apiKey })
  const query = new PostRequest(new Request<VerifyCodeResponse>(endpointUri.href), command)
  const response = await query.response()
  const result = response.data
  return result
}, { returnUri: '' }, {
  immediate: false,
  onError(ex) {
    console.warn(ex)
    onError(ex)
  }
})

export const useCreateLinkCommand = (apiKey: string, onError: (ex: unknown) => void) => useAsyncState<ConfirmationLinkResponse, CreateLinkCommand[]>(async (command) => {
  const endpointUri = useQuery(() => usePathFromBaseURI('avissign', 'Confirmation', 'CreateLink'),
    { name: 'key', value: apiKey }, { name: 'transactionId', value: command.transactionId }, { name: 'email', value: command.email })
  const query = new PostRequest(new Request<ConfirmationLinkResponse>(endpointUri.href), {})
  const response = await query.response()
  const result = response.data
  return result
}, { returnUri: '' }, {
  immediate: false,
  onError(ex) {
    console.warn(ex)
    onError(ex)
  }
})

export const useCreatePayloadCommand = (apiKey: string, onError: (ex: unknown) => void) => useAsyncState<ConfirmationPayload, CreatePayloadCommand[]>(async (command) => {
  const endpointUri = useQuery(() => usePathFromBaseURI('avissign', 'Confirmation', 'CreatePayload'),
    { name: 'key', value: apiKey }, { name: 'data', value: command.data })
  const query = new PostRequest(new Request<ConfirmationPayload>(endpointUri.href), {})
  const response = await query.response()
  const result = response.data
  return result
}, { data: '' }, {
  immediate: false,
  onError(ex) {
    console.warn(ex)
    onError(ex)
  }
})

export const usePhoneConfirmationCommand = (apiKey: string, onError: (ex: unknown) => void) => useAsyncState<ConfirmationPhoneResponse, [string]>(async (payload) => {
  const endpointUri = useQuery(() => usePathFromBaseURI('avissign', 'SmsConfirmation', 'GenerateCodeBySigner'), { name: 'key', value: apiKey })
  const query = new PostRequest(new Request<ConfirmationPhoneResponse>(endpointUri.href), {data: payload})
  const response = await query.response()
  const result = response.data
  return result
}, { data: '' }, {
  immediate: false,
  onError(ex) {
    console.warn(ex)
    onError(ex)
  },
})

export const useSendCodeCommand = (apiKey: string, onError: (ex: unknown) => void) => useAsyncState<VerifyPhoneCodeResponse, VerifyCodeCommand[]>(async (command) => {
  const endpointUri = useQuery(() => usePathFromBaseURI('avissign', 'SmsConfirmation', 'VerifyCode'), { name: 'key', value: apiKey })
  const query = new PostRequest(new Request<VerifyPhoneCodeResponse>(endpointUri.href), command)
  const response = await query.response()
  const result = response.data
  return result
}, { isValid: false }, {
  immediate: false,
  onError(ex) {
    console.warn(ex)
    onError(ex)
  },
})

export const useAvailableClientsCommand = (sessionId: string, senderId: string) => useAsyncState<any, string[]>(async (authorEmail) => {
  const endpointUri = useQuery(() => usePathFromBaseURI('avissign', 'Companies', authorEmail),
    { name: 'transactionId', value: sessionId },
    { name: 'userId', value: senderId },
  )
  const query = new Request(endpointUri.href)
  const response = await query.response()
  const result = response.data
  return result
}, null, {
  immediate: false,
  onError(ex) {
    console.warn(ex)
  },
})
